import type { ChartV2Series } from '../types/chart-v2.types';
import { formatPrice, formatTooltipTime } from '../utils/format';
import { isOhlcPoint, pointValue } from '../utils/helpers';
import type { Scales, SeriesScale, Viewport } from './scales';
import { generateXAxisTicks } from './x-ticks';

export interface DrawTheme {
  background: string;
  /** Default text color for headers / labels (e.g. y-axis unit header). */
  foreground: string;
  gridLine: string;
  axisLine: string;
  axisLabel: string;
  crosshair: string;
  /**
   * Soft surface used for the x-axis crosshair pill. Slightly darker
   * than `background` so the label reads against the chart canvas
   * without needing a border (matches v1's `surface.default` look).
   */
  hoverPillBackground: string;
  seriesDefault: string;
  tooltipBackground: string;
  tooltipForeground: string;
  up: string;
  down: string;
  fontDefault: string;
  fontMono: string;
}

export interface DrawContext {
  ctx: CanvasRenderingContext2D;
  scales: Scales;
  theme: DrawTheme;
  viewport: Viewport;
  showXAxis: boolean;
}

const TARGET_Y_TICKS = 6;
export const Y_AXIS_COLUMN_WIDTH = 56;

const niceStep = (range: number, target: number): number => {
  const step = Math.pow(10, Math.floor(Math.log10(range / target)));
  const err = (target * step) / range;

  if (err <= 0.15) {
    return step * 10;
  }

  if (err <= 0.35) {
    return step * 5;
  }

  if (err <= 0.75) {
    return step * 2;
  }

  return step;
};

const niceTicks = (min: number, max: number, target: number): number[] => {
  if (min === max || !Number.isFinite(min) || !Number.isFinite(max)) {
    return [min];
  }

  const step = niceStep(max - min, target);
  const first = Math.ceil(min / step) * step;
  const ticks: number[] = [];

  for (let t = first; t <= max + step * 1e-9; t += step) {
    ticks.push(Number(t.toFixed(10)));
  }

  return ticks;
};

export const clearCanvas = (
  ctx: CanvasRenderingContext2D,
  viewport: Viewport,
  theme: DrawTheme
): void => {
  ctx.save();
  ctx.fillStyle = theme.background;
  ctx.fillRect(0, 0, viewport.width, viewport.height);
  ctx.restore();
};

export const drawGrid = (dc: DrawContext): void => {
  const { ctx, scales, theme, viewport, showXAxis } = dc;
  const { plot, xDomain } = scales;
  const primary = scales.perSeries[0];
  const bottomY = showXAxis ? plot.y + plot.height : viewport.height;

  ctx.save();
  ctx.strokeStyle = theme.gridLine;
  ctx.lineWidth = 1;
  ctx.setLineDash([]);

  if (primary) {
    const yTicks = niceTicks(
      primary.yDomain[0],
      primary.yDomain[1],
      TARGET_Y_TICKS
    );

    for (const t of yTicks) {
      const y = Math.round(primary.yScale(t)) + 0.5;

      ctx.beginPath();
      ctx.moveTo(plot.x, y);
      ctx.lineTo(plot.x + plot.width, y);
      ctx.stroke();
    }
  }

  const xTicks = generateXAxisTicks(xDomain);

  for (const tick of xTicks) {
    const x = Math.round(scales.xScale(tick.ms)) + 0.5;

    if (x < plot.x - 0.5 || x > plot.x + plot.width + 0.5) {
      continue;
    }

    ctx.beginPath();
    ctx.moveTo(x, 0);
    ctx.lineTo(x, bottomY);
    ctx.stroke();
  }

  ctx.restore();
};

const LABEL_PILL_HEIGHT = 22;
const LABEL_PILL_PADDING_X = 5;
const LABEL_PILL_RADIUS = 3;

/**
 * Draw a rounded rectangle. Uses native `ctx.roundRect` where available
 * (all modern browsers), falls back to manually stroked path.
 */
const drawRoundedRect = (
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number
) => {
  ctx.beginPath();

  if (typeof ctx.roundRect === 'function') {
    ctx.roundRect(x, y, w, h, r);
    return;
  }

  const radius = Math.min(r, w / 2, h / 2);

  ctx.moveTo(x + radius, y);
  ctx.lineTo(x + w - radius, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + radius);
  ctx.lineTo(x + w, y + h - radius);
  ctx.quadraticCurveTo(x + w, y + h, x + w - radius, y + h);
  ctx.lineTo(x + radius, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - radius);
  ctx.lineTo(x, y + radius);
  ctx.quadraticCurveTo(x, y, x + radius, y);
  ctx.closePath();
};

const drawYAxisLabelPill = (
  ctx: CanvasRenderingContext2D,
  theme: DrawTheme,
  label: string,
  centerX: number,
  bottomY: number
) => {
  if (!label) {
    return;
  }

  ctx.save();
  ctx.font = theme.fontDefault;

  const textWidth = ctx.measureText(label).width;
  const pillWidth = Math.ceil(textWidth) + LABEL_PILL_PADDING_X * 2;
  // Center the pill horizontally over its column so it matches the
  // original `AxisUnit` badge (which uses `justify-content: center`
  // inside a fixed-width column).
  const pillX = Math.round(centerX - pillWidth / 2) + 0.5;
  const pillY = Math.round(bottomY - LABEL_PILL_HEIGHT) + 0.5;

  ctx.fillStyle = theme.background;
  ctx.strokeStyle = theme.axisLine;
  ctx.lineWidth = 1;
  drawRoundedRect(ctx, pillX, pillY, pillWidth, LABEL_PILL_HEIGHT, LABEL_PILL_RADIUS);
  ctx.fill();
  ctx.stroke();

  ctx.fillStyle = theme.foreground;
  ctx.textAlign = 'left';
  ctx.textBaseline = 'middle';
  ctx.fillText(label, pillX + LABEL_PILL_PADDING_X, pillY + LABEL_PILL_HEIGHT / 2);
  ctx.restore();
};

const drawOneYAxisColumn = (
  dc: DrawContext,
  opts: {
    seriesScale: SeriesScale;
    seriesLabel: string;
    /**
     * Horizontal centre of the column. Tick labels and the header
     * pill are both centered here so each label has equal slack on
     * its left and right inside the column — important when multiple
     * y-axes stack and the inner column would otherwise read
     * lopsided (right-aligned ticks crowd the separator while leaving
     * a wide gap toward the y-axis line).
     */
    labelCenterX: number;
  }
) => {
  const { seriesScale, seriesLabel, labelCenterX } = opts;
  const { ctx, scales, theme } = dc;
  const { plot } = scales;
  const ticks = niceTicks(
    seriesScale.yDomain[0],
    seriesScale.yDomain[1],
    TARGET_Y_TICKS
  );

  // Column header pill (e.g. "USD", "%", or an indicator short-name).
  // Neutral outline + background mirrors the original `AxisUnit` Badge.
  drawYAxisLabelPill(ctx, theme, seriesLabel, labelCenterX, plot.y - 4);

  ctx.save();
  ctx.font = theme.fontMono;
  ctx.fillStyle = theme.axisLabel;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';

  for (const t of ticks) {
    const y = seriesScale.yScale(t);

    ctx.fillText(formatPrice(t), labelCenterX, y);
  }

  ctx.restore();
};

export interface DrawYAxesOptions {
  /**
   * When true, draw a single y-axis column shared across all series
   * (used by the `indexed` price-display mode). Otherwise each series gets
   * its own column.
   */
  merged?: boolean;
  /** Label used for the shared column when `merged` is true. */
  mergedLabel?: string;
}

export const drawYAxes = (
  dc: DrawContext,
  seriesLabels: string[],
  options: DrawYAxesOptions = {}
): void => {
  const { ctx, scales, theme, viewport, showXAxis } = dc;
  const { plot } = scales;
  const bottomY = showXAxis ? plot.y + plot.height : viewport.height;

  ctx.save();
  ctx.strokeStyle = theme.axisLine;
  ctx.lineWidth = 1;
  ctx.setLineDash([]);
  ctx.beginPath();
  const axisX = Math.round(plot.x + plot.width) + 0.5;

  ctx.moveTo(axisX, 0);
  ctx.lineTo(axisX, bottomY);
  ctx.stroke();
  ctx.restore();

  if (options.merged) {
    const primary = scales.perSeries[0];

    if (!primary) {
      return;
    }

    // Merged column runs from the y-axis line (`plot.x + plot.width`) to
    // the right edge of the single column; center the pill within it.
    const mergedColumnLeft = scales.plot.x + scales.plot.width;
    const mergedColumnRight = mergedColumnLeft + Y_AXIS_COLUMN_WIDTH;

    drawOneYAxisColumn(dc, {
      seriesScale: { ...primary, color: theme.axisLabel },
      labelCenterX: (mergedColumnLeft + mergedColumnRight) / 2,
      seriesLabel: options.mergedLabel ?? '',
    });

    return;
  }

  scales.perSeries.forEach((sc, i) => {
    const label = seriesLabels[i] ?? sc.seriesId;
    const baseColumnX =
      scales.plot.x + scales.plot.width + 8 + i * Y_AXIS_COLUMN_WIDTH;

    if (i > 0) {
      ctx.save();
      ctx.strokeStyle = theme.axisLine;
      ctx.lineWidth = 1;
      ctx.setLineDash([]);
      ctx.beginPath();
      const separatorX = Math.round(baseColumnX - 4) + 0.5;

      ctx.moveTo(separatorX, 0);
      ctx.lineTo(separatorX, bottomY);
      ctx.stroke();
      ctx.restore();
    }

    // Column bounds: the first column starts at the y-axis line; every
    // subsequent column starts 4px after its separator. All columns are
    // `Y_AXIS_COLUMN_WIDTH` wide. Center the pill in that bounding box.
    const columnLeft =
      i === 0 ? scales.plot.x + scales.plot.width : baseColumnX - 4;
    const columnRight = columnLeft + Y_AXIS_COLUMN_WIDTH;

    drawOneYAxisColumn(dc, {
      seriesScale: sc,
      labelCenterX: (columnLeft + columnRight) / 2,
      seriesLabel: label,
    });
  });
};

const drawXAxisLabels = (dc: DrawContext) => {
  const { ctx, scales, theme } = dc;
  const { plot, xDomain } = scales;

  ctx.textAlign = 'center';
  ctx.textBaseline = 'top';

  const xTicks = generateXAxisTicks(xDomain);

  for (const tick of xTicks) {
    const x = scales.xScale(tick.ms);

    if (x < plot.x - 0.5 || x > plot.x + plot.width + 0.5) {
      continue;
    }

    ctx.fillStyle = tick.highlight ? theme.foreground : theme.axisLabel;
    ctx.font = theme.fontDefault;

    ctx.fillText(tick.label, x, plot.y + plot.height + 6);
  }
};

export const drawXAxis = (dc: DrawContext): void => {
  const { ctx, scales, theme } = dc;
  const { plot } = scales;

  ctx.save();
  ctx.strokeStyle = theme.axisLine;

  drawXAxisLabels(dc);

  ctx.beginPath();
  ctx.moveTo(plot.x, plot.y + plot.height + 0.5);
  ctx.lineTo(plot.x + plot.width, plot.y + plot.height + 0.5);
  ctx.stroke();
  ctx.restore();
};

const clipPlot = (dc: DrawContext, fn: () => void) => {
  const { ctx, scales } = dc;
  const { plot } = scales;

  ctx.save();
  ctx.beginPath();
  ctx.rect(plot.x, plot.y, plot.width, plot.height);
  ctx.clip();
  fn();
  ctx.restore();
};

const drawLinePath = (
  dc: DrawContext,
  series: ChartV2Series,
  seriesScale: SeriesScale
) => {
  const { ctx, scales } = dc;

  ctx.strokeStyle = seriesScale.color;
  ctx.lineWidth = 1.5;
  ctx.lineJoin = 'round';
  ctx.beginPath();

  let started = false;

  for (const p of series.data) {
    const x = scales.xScale(p.time);
    const y = seriesScale.yScale(pointValue(p));

    if (!Number.isFinite(x) || !Number.isFinite(y)) {
      continue;
    }

    if (!started) {
      ctx.moveTo(x, y);
      started = true;
    } else {
      ctx.lineTo(x, y);
    }
  }

  ctx.stroke();
};

const fillAreaFromPath = (
  dc: DrawContext,
  series: ChartV2Series,
  seriesScale: SeriesScale
) => {
  const { ctx, scales } = dc;

  if (series.data.length === 0) {
    return;
  }

  const yBase = scales.plot.y + scales.plot.height;
  const xLast = scales.xScale(series.data[series.data.length - 1].time);
  const xFirst = scales.xScale(series.data[0].time);

  ctx.lineTo(xLast, yBase);
  ctx.lineTo(xFirst, yBase);
  ctx.closePath();
  ctx.fillStyle = seriesScale.color;
  ctx.globalAlpha = 0.12;
  ctx.fill();
  ctx.globalAlpha = 1;
};

export const drawLineSeries = (
  dc: DrawContext,
  series: ChartV2Series,
  seriesScale: SeriesScale
): void => {
  clipPlot(dc, () => {
    drawLinePath(dc, series, seriesScale);

    if (series.seriesType === 'area') {
      fillAreaFromPath(dc, series, seriesScale);
    }
  });
};

// Matches the original chart's `getCandleWidth`: leave ~20% of each slot
// as whitespace between adjacent bars, keep wicks a crisp 1 pixel wide.
const BAR_BODY_RATIO = 0.8;
const MIN_BODY_WIDTH_PX = 3;
const WICK_WIDTH = 1;

/**
 * Derive the on-screen body width from the actual pixel spacing between
 * consecutive bars on the x-scale — taking the MINIMUM spacing so
 * intraday gaps (market close, weekends, holidays) don't inflate the
 * body and cause adjacent candles to overlap.
 *
 * 1m example: bars at 09:30, 09:31, … 16:00, then jumps to 09:30 next
 * day. Average spacing balloons from "1 minute" to "≈20 minutes" once
 * an overnight gap is included — so averaging caused bodies to be 20×
 * wider than they should be. Using the min picks the true 1-minute
 * width and adjacent intra-day bars render with the normal 20% gap.
 */
const computeBarBodyWidth = (
  dc: DrawContext,
  ohlc: Array<{ time: number }>
): number => {
  if (ohlc.length < 2) {
    return 6;
  }

  const sampleSize = Math.min(20, ohlc.length - 1);
  let minSpacing = Number.POSITIVE_INFINITY;

  for (let i = 0; i < sampleSize; i++) {
    const spacing = Math.abs(
      dc.scales.xScale(ohlc[i + 1].time) - dc.scales.xScale(ohlc[i].time)
    );

    if (spacing > 0 && spacing < minSpacing) {
      minSpacing = spacing;
    }
  }

  if (!Number.isFinite(minSpacing)) {
    return 6;
  }

  return Math.max(1, Math.round(minSpacing * BAR_BODY_RATIO));
};

const drawOneCandle = (
  dc: DrawContext,
  opts: {
    seriesScale: SeriesScale;
    p: Extract<ChartV2Series['data'][number], { open: number }>;
    bodyWidth: number;
  }
) => {
  const { seriesScale, p, bodyWidth } = opts;
  const { ctx, scales, theme } = dc;
  const x = Math.round(scales.xScale(p.time));
  const yOpen = seriesScale.yScale(p.open);
  const yClose = seriesScale.yScale(p.close);
  const yHigh = seriesScale.yScale(p.high);
  const yLow = seriesScale.yScale(p.low);
  const isUp = p.close >= p.open;
  const color = isUp ? theme.up : theme.down;

  ctx.fillStyle = color;

  const top = Math.round(Math.min(yOpen, yClose));
  const bottom = Math.round(Math.max(yOpen, yClose));
  const barHigh = Math.round(Math.min(yHigh, yLow));
  const barLow = Math.round(Math.max(yHigh, yLow));
  const bodyLeft = x - Math.floor(bodyWidth / 2);

  if (bodyWidth >= MIN_BODY_WIDTH_PX) {
    // Upper wick (above the body) and lower wick (below the body). Use
    // 1-pixel fillRect columns rather than stroke so the wicks stay
    // crisp at any devicePixelRatio.
    ctx.fillRect(x, barHigh, WICK_WIDTH, Math.max(0, top - barHigh));
    ctx.fillRect(x, bottom + 1, WICK_WIDTH, Math.max(0, barLow - bottom));

    // Body: fill the entire bar width.
    ctx.fillRect(bodyLeft, top, bodyWidth, Math.max(1, bottom - top + 1));
    return;
  }

  // Too crowded for a visible body — fall back to a 1px high-to-low line
  // (same behaviour as the original chart when `barWidth < 3`).
  ctx.fillRect(x, barHigh, WICK_WIDTH, Math.max(1, barLow - barHigh));
};

export const drawCandleSeries = (
  dc: DrawContext,
  series: ChartV2Series,
  seriesScale: SeriesScale
): void => {
  const ohlc = series.data.filter(isOhlcPoint);

  if (ohlc.length === 0) {
    return;
  }

  const bodyWidth = computeBarBodyWidth(dc, ohlc);

  clipPlot(dc, () => {
    for (const p of ohlc) {
      drawOneCandle(dc, { seriesScale, p, bodyWidth });
    }
  });
};

const drawOneBar = (
  dc: DrawContext,
  opts: {
    seriesScale: SeriesScale;
    p: Extract<ChartV2Series['data'][number], { open: number }>;
    bodyWidth: number;
  }
) => {
  const { seriesScale, p, bodyWidth } = opts;
  const { ctx, scales, theme } = dc;
  const x = Math.round(scales.xScale(p.time));
  const yOpen = Math.round(seriesScale.yScale(p.open));
  const yClose = Math.round(seriesScale.yScale(p.close));
  const yHigh = Math.round(seriesScale.yScale(p.high));
  const yLow = Math.round(seriesScale.yScale(p.low));
  const isUp = p.close >= p.open;
  const color = isUp ? theme.up : theme.down;

  ctx.strokeStyle = color;
  ctx.lineWidth = 1;

  ctx.beginPath();
  ctx.moveTo(x + 0.5, Math.min(yHigh, yLow));
  ctx.lineTo(x + 0.5, Math.max(yHigh, yLow));

  if (bodyWidth >= MIN_BODY_WIDTH_PX) {
    const tick = Math.max(2, Math.floor(bodyWidth / 2));

    ctx.moveTo(x - tick + 0.5, yOpen);
    ctx.lineTo(x + 0.5, yOpen);
    ctx.moveTo(x + 0.5, yClose);
    ctx.lineTo(x + tick + 0.5, yClose);
  }

  ctx.stroke();
};

export const drawBarSeries = (
  dc: DrawContext,
  series: ChartV2Series,
  seriesScale: SeriesScale
): void => {
  const ohlc = series.data.filter(isOhlcPoint);

  if (ohlc.length === 0) {
    return;
  }

  const bodyWidth = computeBarBodyWidth(dc, ohlc);

  clipPlot(dc, () => {
    for (const p of ohlc) {
      drawOneBar(dc, { seriesScale, p, bodyWidth });
    }
  });
};

export const drawSeries = (dc: DrawContext, series: ChartV2Series[]): void => {
  series.forEach((s, i) => {
    const scale = dc.scales.perSeries[i];

    if (!scale) {
      return;
    }

    if (s.seriesType === 'candlestick') {
      drawCandleSeries(dc, s, scale);
    } else if (s.seriesType === 'bar') {
      drawBarSeries(dc, s, scale);
    } else {
      drawLineSeries(dc, s, scale);
    }
  });
};

const drawLabelPill = (
  ctx: CanvasRenderingContext2D,
  opts: {
    text: string;
    x: number;
    y: number;
    align: 'left' | 'right' | 'center';
    fill: string;
    color: string;
    paddingY?: number;
  }
) => {
  const { text, x, y, align, fill, color, paddingY = 8 } = opts;
  const paddingX = 8;
  const metrics = ctx.measureText(text);
  const textH =
    (metrics.fontBoundingBoxAscent ?? 0) +
    (metrics.fontBoundingBoxDescent ?? 0);
  const w = Math.ceil(metrics.width) + paddingX * 2;
  const h = textH > 0 ? textH + paddingY * 2 : 18 + (paddingY - 3) * 2;

  let left = x;

  if (align === 'right') {
    left = x - w;
  } else if (align === 'center') {
    left = x - w / 2;
  }

  const top = Math.round(y - h / 2);

  ctx.fillStyle = fill;
  ctx.fillRect(left, top, w, h);

  ctx.fillStyle = color;
  ctx.textBaseline = 'middle';
  ctx.textAlign = 'center';
  ctx.fillText(text, left + w / 2, top + h / 2);

  return { left, top, width: w, height: h };
};

export interface HoverHit {
  seriesId: string;
  color: string;
  value: number;
}

export interface DrawHoverOverlayInput {
  hoverTime: number;
  hits: HoverHit[];
  seriesLabels: string[];
  showTimeLabel?: boolean;
  isIntraday?: boolean;
}

export interface DrawLatestPriceInput {
  values: (number | undefined)[];
}

export const drawLatestPriceOverlay = (
  dc: DrawContext,
  input: DrawLatestPriceInput
): void => {
  const { ctx, scales } = dc;
  const { plot } = scales;

  ctx.save();
  ctx.font = dc.theme.fontMono;

  scales.perSeries.forEach((sc, i) => {
    const value = input.values[i];

    if (value == null || !Number.isFinite(value)) {
      return;
    }

    const y = sc.yScale(value);

    if (!Number.isFinite(y) || y < plot.y || y > plot.y + plot.height) {
      return;
    }

    ctx.strokeStyle = sc.color;
    ctx.lineWidth = 1;
    ctx.setLineDash([4, 4]);
    ctx.beginPath();
    ctx.moveTo(plot.x, Math.round(y) + 0.5);
    ctx.lineTo(plot.x + plot.width, Math.round(y) + 0.5);
    ctx.stroke();

    ctx.setLineDash([]);

    const columnX = plot.x + plot.width + 8 + i * Y_AXIS_COLUMN_WIDTH;
    const pillX = columnX - 4;

    drawLabelPill(ctx, {
      text: formatPrice(value),
      x: pillX,
      y,
      align: 'left',
      fill: sc.color,
      color: dc.theme.background,
      paddingY: 2,
    });
  });

  ctx.restore();
};

export const drawHoverOverlay = (
  dc: DrawContext,
  input: DrawHoverOverlayInput
): void => {
  const { ctx, scales, theme } = dc;
  const { plot } = scales;
  const x = scales.xScale(input.hoverTime);

  if (!Number.isFinite(x) || x < plot.x || x > plot.x + plot.width) {
    return;
  }

  ctx.save();

  ctx.strokeStyle = theme.crosshair;
  ctx.lineWidth = 1;
  ctx.setLineDash([4, 4]);
  ctx.beginPath();
  ctx.moveTo(Math.round(x) + 0.5, plot.y);
  ctx.lineTo(Math.round(x) + 0.5, plot.y + plot.height);
  ctx.stroke();

  scales.perSeries.forEach((sc, i) => {
    const hit = input.hits.find(h => h.seriesId === sc.seriesId);

    if (!hit) {
      return;
    }

    const y = sc.yScale(hit.value);

    if (!Number.isFinite(y) || y < plot.y || y > plot.y + plot.height) {
      return;
    }

    ctx.strokeStyle = sc.color;
    ctx.setLineDash([4, 4]);
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(plot.x, Math.round(y) + 0.5);
    ctx.lineTo(plot.x + plot.width, Math.round(y) + 0.5);
    ctx.stroke();

    ctx.setLineDash([]);
    ctx.font = theme.fontMono;

    const columnX = plot.x + plot.width + 8 + i * Y_AXIS_COLUMN_WIDTH;
    const pillX = columnX - 4;

    drawLabelPill(ctx, {
      text: formatPrice(hit.value),
      x: pillX,
      y,
      align: 'left',
      fill: theme.hoverPillBackground,
      color: sc.color,
      paddingY: 2,
    });
  });

  if (input.showTimeLabel !== false) {
    ctx.setLineDash([]);
    ctx.font = theme.fontDefault;

    // Match v1's `drawXAxisMark`: a soft surface pill with no border,
    // sitting just above the x-axis tick row.
    const rawLabel = formatTooltipTime(input.hoverTime);
    const timeLabel =
      input.isIntraday === false
        ? rawLabel.split(' ').slice(0, 3).join(' ')
        : rawLabel;

    drawLabelPill(ctx, {
      text: timeLabel,
      x,
      y: plot.y + plot.height + 12,
      align: 'center',
      fill: theme.hoverPillBackground,
      color: theme.foreground,
    });
  }

  ctx.restore();
};

export const DEFAULT_DRAW_THEME: DrawTheme = {
  background: '#fcfcfc',
  foreground: '#202020',
  gridLine: '#e8e8e8',
  axisLine: '#9aa0a6',
  axisLabel: '#646464',
  crosshair: '#202020',
  hoverPillBackground: '#f0f0f0',
  seriesDefault: '#ffc53d',
  tooltipBackground: '#ffffff',
  tooltipForeground: '#202020',
  up: '#53b365',
  down: '#ec6142',
  fontDefault: '12px Nort, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  fontMono: '12px Roboto Mono, ui-monospace, monospace',
};

const readCssVar = (el: HTMLElement, name: string): string | null => {
  const v = getComputedStyle(el).getPropertyValue(name).trim();

  return v ? v : null;
};

const pickVar = (
  el: HTMLElement,
  names: string[],
  fallback: string
): string => {
  for (const name of names) {
    const v = readCssVar(el, name);

    if (v) {
      return v;
    }
  }

  return fallback;
};

export const resolveThemeFromElement = (el: HTMLElement): DrawTheme => ({
  background: pickVar(
    el,
    ['--chartv2-background', '--background-default', '--surface-default'],
    DEFAULT_DRAW_THEME.background
  ),
  foreground: pickVar(
    el,
    ['--chartv2-foreground', '--text-default'],
    DEFAULT_DRAW_THEME.foreground
  ),
  gridLine: pickVar(
    el,
    ['--chartv2-grid-line', '--border-soft'],
    DEFAULT_DRAW_THEME.gridLine
  ),
  axisLine: pickVar(
    el,
    ['--chartv2-axis-line', '--border-default'],
    DEFAULT_DRAW_THEME.axisLine
  ),
  axisLabel: pickVar(
    el,
    ['--chartv2-axis-label', '--text-soft'],
    DEFAULT_DRAW_THEME.axisLabel
  ),
  crosshair: pickVar(
    el,
    ['--chartv2-crosshair', '--text-default'],
    DEFAULT_DRAW_THEME.crosshair
  ),
  hoverPillBackground: pickVar(
    el,
    ['--chartv2-hover-pill-background', '--surface-default'],
    DEFAULT_DRAW_THEME.hoverPillBackground
  ),
  seriesDefault: pickVar(
    el,
    [
      '--chartv2-series-default',
      '--chart-multi-lines-hex-1',
      '--border-primary',
    ],
    DEFAULT_DRAW_THEME.seriesDefault
  ),
  tooltipBackground: pickVar(
    el,
    ['--chartv2-tooltip-background', '--surface-raised'],
    DEFAULT_DRAW_THEME.tooltipBackground
  ),
  tooltipForeground: pickVar(
    el,
    ['--chartv2-tooltip-foreground', '--text-default'],
    DEFAULT_DRAW_THEME.tooltipForeground
  ),
  up: pickVar(
    el,
    ['--chartv2-up', '--viz-bullish', '--green--5'],
    DEFAULT_DRAW_THEME.up
  ),
  down: pickVar(
    el,
    ['--chartv2-down', '--viz-bearish', '--red--5'],
    DEFAULT_DRAW_THEME.down
  ),
  fontDefault: pickVar(
    el,
    ['--chartv2-font-default'],
    DEFAULT_DRAW_THEME.fontDefault
  ),
  fontMono: pickVar(
    el,
    ['--chartv2-font-mono'],
    DEFAULT_DRAW_THEME.fontMono
  ),
});
